import {
    HttpTransportType,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from "@microsoft/signalr";
import lodash from "lodash";
import RatioProcessor from "./RatioProcessor";

const isNegotiationAbortError = (err) => {
    const message = (err && (err.message || err.toString())) || "";
    return message.includes("stopped during negotiation") || err?.name === "AbortError";
};

export default class Streaming {
    constructor() {
        this.streamObj = { Platform: "hisselab", EntityIds: [], EndOfDay: false };
        this.historyArgs = {
            Platform: "hisselab",
            EntityId: "",
            StartDate: "",
            EndDate: "",
            Resolution: "",
            EndOfDay: false,
        };
        this.infoObj = { Platform: "hisselab", EntityId: "", LanguageId: null };
        this.callbackFunctions = { onHistoryCallback: null, onInfoCallback: null };
        this.observers = [];
        this.ratioProcessor = new RatioProcessor();
        this.isInvoked = false;
        this.debouncedStreamInvoke = lodash.debounce(
            () => this.streamInvoke(),
            500,
            { leading: true }
        );
        this.buildConnect();
    }

    buildConnect() {
        this.hubConnection = new HubConnectionBuilder()
            .configureLogging(LogLevel.Information)
            .withUrl("https://api.rasyonet.com/service-realtime-preprod/hubs/realtime", {
                withCredentials: false,
                transport: HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect([0, 3000, 5000, 10000, 15000, 30000])
            .build();
        this.registerHandlers();
        this.startConnection().catch(err =>
            !isNegotiationAbortError(err) && console.error("Connection start failed:", err)
        );
    }

    registerHandlers() {
        const normalizeStreamMessage = (msg) => {
            if (msg && msg["@"] === undefined) {
                const id = msg.key !== undefined
                    ? msg.key
                    : msg.Key !== undefined
                        ? msg.Key
                        : msg.id !== undefined
                            ? msg.id
                            : msg.EntityId !== undefined
                                ? msg.EntityId
                                : msg.entityId !== undefined
                                    ? msg.entityId
                                    : msg.objectId !== undefined
                                        ? msg.objectId
                                        : msg.ObjectId;
                if (id !== undefined) msg["@"] = id;
            }
            if (msg) {
                if (msg.lastUpdateDate === undefined && msg.lastUpdateAt !== undefined) {
                    const updateAt = msg.lastUpdateAt;
                    msg.lastUpdateDate = typeof updateAt === "string" && updateAt.includes(" ")
                        ? updateAt.split(" ")[0]
                        : updateAt;
                }
                if (msg.lastTradeTime === undefined && msg.lastTradeAt !== undefined) {
                    const tradeAt = msg.lastTradeAt;
                    msg.lastTradeTime = typeof tradeAt === "string" && tradeAt.includes(" ")
                        ? tradeAt.split(" ")[1]
                        : tradeAt;
                }
            }
            return msg;
        };

        const handleStreamPayload = (raw) => {
            try {
                const parsed = JSON.parse(raw);
                const items = Array.isArray(parsed) ? parsed : [parsed];
                const notifyObservers = (payload) => {
                    const id = payload && payload["@"]; 
                    if (!id) return;
                    this.observers.forEach((obs) => {
                        if (obs.objectIds.includes(id)) obs.onMessage(payload);
                    });
                };

                items.forEach((item) => {
                    const msg = normalizeStreamMessage(item);
                    const id = msg && msg["@"]; 
                    if (!id) return;

                    if (!this.ratioProcessor || msg.lastPrice === undefined || msg.lastPrice === null) {
                        notifyObservers(msg);
                        return;
                    }

                    Promise.resolve(this.ratioProcessor.calculateFields(id, msg))
                        .then((enriched) => {
                            notifyObservers(enriched || msg);
                        })
                        .catch((err) => {
                            console.warn("RatioProcessor error:", err);
                            notifyObservers(msg);
                        });
                });
            } catch (err) {
                console.error("Error in updates handler:", err);
            }
        };

        this.hubConnection.on("updates", handleStreamPayload);
        this.hubConnection.on("snapshots", handleStreamPayload);

        this.hubConnection.on("symbolInfos", (data) => {
            try {
                const parsed = JSON.parse(data);
                const info = Array.isArray(parsed) ? parsed[0] : parsed;
                const payload = info && info.info ? info.info : info;
                this.callbackFunctions.onInfoCallback &&
                    this.callbackFunctions.onInfoCallback(payload);
            } catch (err) {
                console.error("Error in SymbolInfo handler:", err);
            }
        });

        this.hubConnection.on("histories", (data) => {
            try {
                const parsed = JSON.parse(data);
                const bars = [];
                const meta = { noData: true };
                for (const s of parsed.series || []) {
                    bars.push({
                        time: s.t * 1000,
                        open: s.o,
                        high: s.h,
                        low: s.l,
                        close: s.c,
                        volume: s.v,
                    });
                    meta.noData = false;
                }
                if (meta.noData && parsed.range) {
                    if (this.historyArgs.EndDate >= parsed.range.start) {
                        meta.nextTime = parsed.range.end;
                    }
                }
                this.callbackFunctions.onHistoryCallback &&
                    this.callbackFunctions.onHistoryCallback(bars, meta);
            } catch (err) {
                console.error("Error in History handler:", err);
            }
        });

        this.hubConnection.on("errors", (data) => {
            console.error("Hub error:", data);
        });

        this.hubConnection.onreconnecting((err) =>
            console.warn("Reconnecting...", err)
        );
        this.hubConnection.onreconnected(() => {
            console.log("Reconnected, resubscribing...");
            this.isInvoked = false;
            this.invoker();
        });
        this.hubConnection.onclose((err) =>
            console.warn("Connection closed:", err)
        );
    }

    startConnection() {
        if (this.hubConnection.state === HubConnectionState.Disconnected) {
            return this.hubConnection.start().then(() => {
                console.log("Hub connection started");
            });
        }
        return Promise.resolve();
    }

    invokeWithRetry(method, payload, retries = 3, backoff = 1000) {
        const attempt = n => {
            return this.hubConnection
                .invoke(method, JSON.stringify(payload))
                .catch(err => {
                    if (n < retries) {
                        console.warn(`${method} failed, retry ${n}`, err);
                        return new Promise(res => setTimeout(res, backoff * n))
                            .then(() => attempt(n + 1));
                    }
                    throw err;
                });
        };
        return attempt(1);
    }

    subscribe(observer, onMessage) {
        observer.onMessage = onMessage;
        this.observers.push(observer);
        this.isInvoked = false;
        this.debouncedStreamInvoke();
        return new Unsubscriber(this.observers, observer, this);
    }

    unsubscribe(objectIds) {
        const ids = Array.isArray(objectIds) ? objectIds : [objectIds];
        if (!ids.length || this.hubConnection.state !== HubConnectionState.Connected) {
            return;
        }
        ids.filter(Boolean).forEach(id => {
            this.hubConnection
                .invoke("Unsubscribe", this.streamObj.Platform, id)
                .catch(err => console.error("Unsubscribe error:", err));
        });
    }

    streamInvoke(isForceClose = false) {
        const objectIds = this.observers.reduce((acc, obs) =>
            lodash.union(acc, obs.objectIds), []);
        this.streamObj.EntityIds = objectIds;
        if (lodash.isEmpty(objectIds)) {
            this.hubConnection.stop().catch(err =>
                console.warn("Stop failed:", err)
            );
            console.log("No objectIds → stopped streaming");
            return;
        }
        if (isForceClose) { this.resetStream(); return; }
        if (this.hubConnection.state === HubConnectionState.Connected) {
            this.invoker();
        } else if (this.hubConnection.state === HubConnectionState.Disconnected) {
            this.startConnection()
                .then(() => this.invoker())
                .catch(err => {
                    if (!isNegotiationAbortError(err)) {
                        console.error("Restart failed:", err);
                    }
                });
        } else {
            setTimeout(() => this.streamInvoke(), 1000);
        }
    }

    invoker() {
        if (this.isInvoked) return;
        this.isInvoked = true;
        this.invokeWithRetry("Subscribe", this.streamObj)
            .catch(err => console.error("Final Subscribe error:", err));
    }

    resetStream() {
        this.hubConnection
            .stop()
            .then(() => this.hubConnection.start())
            .then(() => {
                console.log("Reset and restarted");
                this.isInvoked = false;
                this.invoker();
            })
            .catch(err => console.error("ResetStream error:", err));
    }

    symbolInfo(symbol, onInfoCallback, onErrorCallback) {
        this.callbackFunctions.onInfoCallback = onInfoCallback;
        if (!symbol) return;
        this.infoObj.EntityId = symbol;
        const call = () =>
            this.invokeWithRetry("SymbolInfo", this.infoObj).catch(onErrorCallback);
        if (this.hubConnection.state !== HubConnectionState.Connected) {
            setTimeout(() =>
                this.startConnection().then(call).catch(onErrorCallback), 1000
            );
        } else { call(); }
    }

    history(args, onHistoryCallback, onErrorCallback) {
        this.callbackFunctions.onHistoryCallback = onHistoryCallback;
        if (!args.EntityId && !args.Symbol) return;
        const entityId = args.EntityId || args.Symbol;
        this.historyArgs = {
            Platform: "hisselab",
            EntityId: entityId,
            StartDate: args.StartDate,
            EndDate: args.EndDate,
            Resolution: args.Resolution,
            EndOfDay: args.EndOfDay || false,
        };
        if (args.FieldType) this.historyArgs.FieldType = args.FieldType;
        const call = () =>
            this.invokeWithRetry("History", this.historyArgs).catch(onErrorCallback);
        if (this.hubConnection.state !== HubConnectionState.Connected) {
            setTimeout(() =>
                this.startConnection().then(call).catch(onErrorCallback), 1000
            );
        } else { call(); }
    }
}

export class Unsubscriber {
    constructor(observers, observer, streaming) {
        this._observers = observers;
        this._observer = observer;
        this._streaming = streaming;
    }

    Dispose() {
        const idx = this._observers.indexOf(this._observer);
        if (idx !== -1) {
            this._streaming.unsubscribe(this._observer.objectIds);
            this._observers.splice(idx, 1);
            this._streaming.streamInvoke(true);
            this._streaming.isInvoked = false;
        }
    }
}


